import React from "react";
import cn from "./helpers/cn";

type LabelPlacement = "end" | "start" | "top" | "bottom";

export interface FormControlLabelProps {
  control: React.ReactElement;
  label: React.ReactNode;
  value?: unknown;
  checked?: boolean;
  name?: string;
  disabled?: boolean;
  required?: boolean;
  labelPlacement?: LabelPlacement;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => void;
  inputRef?: React.Ref<HTMLInputElement>;
  className?: string;
  style?: React.CSSProperties;
  labelClassName?: string;
}

const placementMap: Record<LabelPlacement, string> = {
  end: "flex-row",
  start: "flex-row-reverse",
  top: "flex-col-reverse",
  bottom: "flex-col",
};

const FormControlLabel = React.forwardRef<HTMLLabelElement, FormControlLabelProps>((props, ref) => {
  const {
    control,
    label,
    value,
    checked,
    name,
    disabled = false,
    required = false,
    labelPlacement = "end",
    onChange,
    inputRef,
    className,
    style,
    labelClassName,
  } = props;

  // Props injetadas no control só quando definidas em FormControlLabel —
  // assim o componente filho preserva os defaults dele para o que não foi passado.
  const injected: Record<string, unknown> = {};

  if (value !== undefined) injected.value = value;
  if (checked !== undefined) injected.checked = checked;
  if (name !== undefined) injected.name = name;
  if (onChange !== undefined) injected.onChange = onChange;
  if (inputRef !== undefined) injected.inputRef = inputRef;
  injected.disabled = disabled || (control.props as { disabled?: boolean }).disabled;

  const clonedControl = React.cloneElement(control, injected);

  return (
    <label
      ref={ref}
      style={style}
      className={cn(
        "inline-flex select-none items-center gap-2 align-middle",
        placementMap[labelPlacement],
        disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer",
        className
      )}
    >
      {clonedControl}

      <span className={cn("text-sm leading-none", labelClassName)}>
        {label}
        {required && <span className="ml-0.5 text-red-500">*</span>}
      </span>
    </label>
  );
});

FormControlLabel.displayName = "FormControlLabel";

export default FormControlLabel;
